import api from './api'

export type LaborTimescale = 'L1' | 'L7' | 'L30' | 'YTD' | 'LTM'

export const LABOR_TIMESCALES: LaborTimescale[] = ['L1', 'L7', 'L30', 'YTD', 'LTM']

export const LABOR_STORE_NAMES = ['River Oaks', 'Highland Park'] as const
export type LaborStoreName = (typeof LABOR_STORE_NAMES)[number]
export type LaborStoreFilter = 'all' | LaborStoreName

export type LaborChartMetric =
  | 'gross_sales_per_hour'
  | 'regular_hours_by_station'
  | 'overtime_hours_by_station'
  | 'wages_by_station'
  | 'avg_hourly_wages'
  | 'wages_pct_of_sales'
  | 'wages_pct_of_station_sales'

export type LaborStationFilter = 'all' | (string & {})

export interface LaborPoint {
  date: string
  value?: string | null
  numerator?: string | null
  denominator?: string | null
}

export interface LaborSeries {
  key: string
  points: LaborPoint[]
}

export interface LaborChartResponse {
  metric: string
  store: string
  timescale: LaborTimescale
  start: string
  end: string
  series: LaborSeries[]
}

export interface WeeklyOtEmployeeRow {
  week_ended: string
  store: string
  bucket: 'top' | 'all_other'
  rank?: number | null
  employee_id?: string | null
  first_name?: string | null
  last_name?: string | null
  role?: string | null
  ot_hours: string
  other_count?: number | null
  avg_ot_per_employee?: string | null
}

export interface WeeklyOtEmployeeResponse {
  store: string
  start: string
  end: string
  rows: WeeklyOtEmployeeRow[]
}

export interface WeeklyOtStationRow {
  week_ended: string
  store: string
  station: string
  ot_hours: string
  total_hours: string
  fte: string
  avg_ot_per_fte?: string | null
}

export interface WeeklyOtStationResponse {
  store: string
  start: string
  end: string
  rows: WeeklyOtStationRow[]
}

export interface LaborChartQuery {
  stores?: string[]
  stations?: string[]
  store?: string
  station?: string
  start: string
  end: string
  timescale?: LaborTimescale
  force?: boolean
}

export interface LaborOvertimeQuery {
  stores?: string[]
  stations?: string[]
  store?: string
  station?: string
  start: string
  end: string
}

export function selectedLaborNames(values: string[] | string | undefined): string[] {
  const list = Array.isArray(values) ? values : values != null && values !== '' ? [values] : []
  return list.map((name) => name.trim()).filter((name) => name !== '' && name !== 'all')
}

export function laborStoreCacheToken(values: string[] | string | undefined): string {
  const names = [...new Set(selectedLaborNames(values))]
  if (names.length === 0) return 'all'
  if (LABOR_STORE_NAMES.every((name) => names.includes(name))) return 'all'
  return names.sort((a, b) => a.localeCompare(b)).join(',')
}

export function laborStationCacheToken(values: string[] | string | undefined): string {
  const names = [...new Set(selectedLaborNames(values))]
  if (names.length === 0) return 'all'
  return names.sort((a, b) => a.localeCompare(b)).join(',')
}

function laborFilterParams(query: LaborChartQuery | LaborOvertimeQuery): {
  store: string[]
  station: string[]
} {
  return {
    store: selectedLaborNames(query.stores ?? query.store),
    station: selectedLaborNames(query.stations ?? query.station),
  }
}

export function buildLaborAnalyticsQuery(
  params: Record<string, string | string[] | boolean | undefined>,
): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue
    if (typeof value === 'boolean') {
      if (value) search.set(key, 'true')
      continue
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== '') search.append(key, item)
      }
      continue
    }
    if (value !== '') search.set(key, value)
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export async function fetchLaborChart(
  metric: LaborChartMetric,
  query: LaborChartQuery,
): Promise<LaborChartResponse> {
  const filters = laborFilterParams(query)
  const qs = buildLaborAnalyticsQuery({
    store: filters.store,
    station: filters.station,
    start: query.start,
    end: query.end,
    timescale: query.timescale ?? 'L7',
  })
  const response = await api.get<LaborChartResponse>(
    `/labor-analytics/charts/${metric}${qs}`,
  )
  return response.data
}

export async function fetchWeeklyOtEmployees(
  query: LaborOvertimeQuery,
): Promise<WeeklyOtEmployeeResponse> {
  const filters = laborFilterParams(query)
  const qs = buildLaborAnalyticsQuery({
    store: filters.store,
    station: filters.station,
    start: query.start,
    end: query.end,
  })
  const response = await api.get<WeeklyOtEmployeeResponse>(
    `/labor-analytics/overtime/employees${qs}`,
  )
  return response.data
}

export interface LaborRefreshResponse {
  charts?: Record<LaborChartMetric, LaborChartResponse>
  charts_by_store?: Record<string, Record<LaborChartMetric, LaborChartResponse>>
  overtime_employees: WeeklyOtEmployeeResponse
  overtime_stations: WeeklyOtStationResponse
  incomplete_roles?: string[]
  stations?: string[]
}

export async function fetchLaborRefresh(
  query: LaborChartQuery,
): Promise<LaborRefreshResponse> {
  const filters = laborFilterParams(query)
  const qs = buildLaborAnalyticsQuery({
    store: filters.store,
    station: filters.station,
    start: query.start,
    end: query.end,
    timescale: query.timescale ?? 'L7',
    force: query.force,
  })
  const response = await api.post<LaborRefreshResponse>(`/labor-analytics/refresh${qs}`)
  return response.data
}

export type LaborAnalyticsSheetExportRequest = {
  title?: string
  headers: string[]
  rows: Array<Array<string | number>>
}

export type LaborAnalyticsSheetExportResponse = {
  spreadsheet_id: string
  url: string
  name: string
}

export async function exportLaborAnalyticsSheet(
  payload: LaborAnalyticsSheetExportRequest,
): Promise<LaborAnalyticsSheetExportResponse> {
  const { data } = await api.post<LaborAnalyticsSheetExportResponse>(
    '/labor-analytics/export-sheet',
    payload,
  )
  return data
}
